// Small pure helpers for the Conversations inbox.

export function initials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '?';
}

export function relativeTime(ms?: number | null): string {
  if (ms == null) return '';
  const diff = Date.now() - ms;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function fmtDateTime(ms?: number | null): string {
  if (ms == null) return '';
  return new Date(ms).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export function dayKey(ms?: number | null): string {
  if (ms == null) return '';
  return new Date(ms).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

export function truncate(text?: string | null, max = 80): string {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '…' : text;
}

// Title-case a name only if it's entirely lowercase (some GHL records store
// names lowercased); leave properly-cased names (e.g. "McDonald") untouched.
export function displayName(name?: string | null): string {
  const s = (name || '').trim();
  if (!s) return 'Unknown';
  if (s !== s.toLowerCase()) return s;
  return s.replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

export const CHANNEL_LABEL: Record<string, string> = {
  sms: 'SMS', email: 'Email', call: 'Call', voicemail: 'Voicemail',
  fb: 'Facebook', ig: 'Instagram', whatsapp: 'WhatsApp', webchat: 'Web chat', other: 'Other',
};

// Channels the composer can actually send on.
export const SENDABLE_CHANNELS = new Set(['sms', 'email']);

// Apex sending lines, for the number display + "Send as" override.
// Robert's confirmed from message data; Joseph's pending his confirmation.
// Fill JOSEPH with his GHL SMS number (E.164) to light up "Send as Joseph".
export const SENDING_NUMBERS: { broker: string; number: string }[] = [
  { broker: 'Joseph', number: '+19094066538' },
  { broker: 'Robert', number: '+19519773251' },
];

const digits = (s?: string | null) => (s || '').replace(/\D/g, '');

// Map an E.164 line to the broker who owns it (else null).
export function brokerForNumber(num?: string | null): string | null {
  if (!num) return null;
  const d = digits(num).slice(-10);
  const hit = SENDING_NUMBERS.find((n) => digits(n.number).slice(-10) === d);
  return hit ? hit.broker : null;
}

// Pretty-print an E.164 US number as (xxx) xxx-xxxx.
export function fmtPhone(num?: string | null): string {
  const d = digits(num).slice(-10);
  if (d.length !== 10) return num || '';
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
